import { CircularProgress, Grid, IconButton, Tooltip } from '@mui/material';
import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { CardActionArea } from '@mui/material';
import React, { useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

//import ReactDOMServer from 'react-dom/server';
//import { QuillDeltaToHtmlConverter, GroupType } from 'quill-delta-to-html';

import {
  IPromptCard,
  IDelta,
  IImage,
  IUserDocumentSection,
  IPromptBlock,
} from '@homeporter/types';

import { Delete } from '@mui/icons-material';
import { StyledImageOverlay } from './styles';
import QuillEditor, { RefAttr } from './QuillEditor';
import { StyledResults } from './styles';

import { marked } from 'marked';
import { Edit } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { ISelectedCard } from '../../../../global/interfaces';
import { uuid } from '../../../../utils/helpers';
import { parseValidPromptBlocks } from '../../../../utils/helpers/parser';
import { SIZE_WHITELIST } from '../../../../utils/helpers';
import {
  nodesToDelta,
  INode,
  convertDeltaToHtmlWithRefs,
} from '../../../../utils/helpers/quill';

const sizeMap = Object.fromEntries(
  SIZE_WHITELIST.map((v) => [v, `font-size:${v}`]),
);

const PulsingSkeleton = styled(Skeleton)(() => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%', // start off-screen
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent)',
    animation: 'pulse-sweep 2s linear infinite',
  },
  '@keyframes pulse-sweep': {
    '0%': { left: '-100%' },
    '100%': { left: '100%' },
  },
}));

const noText = (
  <>
    <PulsingSkeleton variant="rounded" width={'100%'} height={25} />
    <br />
    <PulsingSkeleton variant="rounded" width={'100%'} height={25} />
    <br />
    <PulsingSkeleton variant="rounded" width={'50%'} height={25} />
  </>
);

const renderPromptBlocks = (
  card: IPromptCard,
  styles: any,
  blocks: IPromptBlock[] | null,
  highlightedSids: string[],
  setHighlightedSids: (sids: string[]) => void,
): React.ReactNode => {
  return blocks?.map((item, index) => {
    let dataId: string | null = null;
    if (item.ref && item.ref?.length > 0) {
      dataId = uuid();
      card.refs = { ...(card.refs || {}), [dataId]: item.ref };
    }

    console.log('::::item: ', item);
    return (
      <>
        <Box
          data-id={dataId}
          component={item.type as any}
          key={index}
          sx={{ display: 'block' }}>
          {item.text}
          {dataId ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setHighlightedSids(
                  highlightedSids === item.ref ? [] : item.ref || [],
                );
              }}
              sx={{
                ...styles.iconButton,

                backgroundColor:
                  highlightedSids === item.ref
                    ? '#ffdab3'
                    : highlightedSids === item.ref
                      ? '#e8f7f8'
                      : 'inherit',
              }}>
              <LinkIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
        {item?.children?.length && item?.children?.length > 0 ? (
          <ul>
            {renderPromptBlocks(
              card,
              styles,
              item.children,
              highlightedSids,
              setHighlightedSids,
            )}
          </ul>
        ) : null}
      </>
    );
  });
};

interface PromptBlockProps {
  index: number;
  styles: any;
  promptCard: IPromptCard;
  documentSection: IUserDocumentSection;
  setDocumentSection: (section: IUserDocumentSection) => void;

  setSelectedCard: (card: ISelectedCard | null) => void;
  setSelectedImage: (image: IImage | null) => void;

  enableQuill: boolean;
  quillRef: React.RefObject<any>;
  setSaveEnabled: (enabled: boolean) => void;

  imagesMapRef: Map<string, IImage> | null;
  highlightedSids: string[];
  setHighlightedSids: Dispatch<SetStateAction<string[]>>;
  regenerateIndex: number | undefined;
}

const PromptBlock = ({
  index,
  styles,
  promptCard,
  documentSection,
  setDocumentSection,

  setSelectedCard,
  setSelectedImage,

  enableQuill,
  quillRef,
  setSaveEnabled,

  imagesMapRef,
  highlightedSids,
  setHighlightedSids,
  regenerateIndex,
}: PromptBlockProps) => {
  const _enableQuill = enableQuill && regenerateIndex === undefined;
  const [result, setResult] = useState<IPromptBlock[] | null>([]);
  const [rawHtml, setRawHtml] = useState<string | null>(null);

  const [hoveredSection, setHoveredSection] = useState<number | null>(null);
  const [draggedImageIndices, setDraggedImageIndices] = useState<{
    sectionIndex: number;
    imageIndex: number;
  } | null>(null);

  const handleDragStart = (index: number, imageIndex: number) => {
    console.log('::::handleDragStart: ', index, imageIndex);
    setDraggedImageIndices({ sectionIndex: index, imageIndex: imageIndex });
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault();
    setHoveredSection(index);
  };

  const handleDragLeave = () => {
    setHoveredSection(null);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    sectionIndex: number,
    imageIndex?: number,
  ) => {
    console.log(
      '::::handleDrop: ',
      e,
      `sectionIndex: ${sectionIndex}`,
      `imageIndex: ${imageIndex}`,
      documentSection?.cards[sectionIndex],
    );
    e.preventDefault();
    if (
      !documentSection?.cards ||
      documentSection?.cards[sectionIndex].type !== 'Prompt'
    )
      return;

    const targetCard = documentSection?.cards[sectionIndex] as IPromptCard;

    if (draggedImageIndices !== null) {
      if (
        documentSection?.cards[draggedImageIndices.sectionIndex].type !==
        'Prompt'
      )
        return;

      const prompCard = documentSection?.cards[
        draggedImageIndices.sectionIndex
      ] as IPromptCard;
      const imageId = prompCard.images[draggedImageIndices.imageIndex];

      prompCard.images.splice(draggedImageIndices.imageIndex, 1);
      targetCard.images.push(imageId);
    } else {
      const imageId = e.dataTransfer.getData('text/plain');

      if (!targetCard.images.find((img) => img.id === imageId)) {
        const image = imagesMapRef?.get(imageId);
        if (image) targetCard.images.push(image);
      }
    }

    setHoveredSection(null);
    setDraggedImageIndices(null);
    setSaveEnabled(true);
  };

  const handleRemoveImage = (
    event: any,
    cardIndex: number,
    imageIndex: number,
  ) => {
    if (
      !documentSection?.cards ||
      documentSection.cards[cardIndex].type !== 'Prompt'
    )
      return;

    const targetCard = documentSection.cards[cardIndex] as IPromptCard;
    const images = targetCard.images.filter((image, idx) => idx !== imageIndex);
    targetCard.images = images || [];
    console.log('::::setDocument section 15...');
    setDocumentSection({ ...documentSection });
    console.log(':::::documentSection.cards', documentSection.cards);
  };

  const handleSelectImage = (
    event: any,
    sectionIndex: number,
    imageIndex: number,
  ) => {
    if (
      !documentSection?.cards ||
      documentSection.cards[sectionIndex].type !== 'Prompt'
    )
      return;

    const img = (documentSection.cards[sectionIndex] as IPromptCard).images[
      imageIndex
    ];
    if (!img?.id) return;

    const image = imagesMapRef?.get(img.id);

    console.log((documentSection.cards[sectionIndex] as IPromptCard).images);
    console.log('select image: ', image);

    setSelectedCard(null);
    setSelectedImage(image || null);
  };

  useEffect(() => {
    console.log('::::useEffect...');
    if (!promptCard) {
      setRawHtml('');
      return;
    }

    const processPromptCard = async () => {
      //await new Promise((resolve) => setTimeout(resolve, 250));
      console.log('::::promptCard: ', promptCard);

      if (_enableQuill) {
        if (promptCard?.structured && !promptCard.modified) {
          const parsedObjects = parseValidPromptBlocks(promptCard['response']);
          promptCard.modified = nodesToDelta(parsedObjects as INode[]);
        }
        setRawHtml('');
        return;
      }

      if (
        typeof promptCard['modified'] !== 'string' &&
        (promptCard['modified'] as IDelta)?.ops
      ) {
        const delta = promptCard['modified'] as IDelta;
        const html = convertDeltaToHtmlWithRefs(delta.ops as any, sizeMap);
        setRawHtml(html);

        /*
        const converter = new QuillDeltaToHtmlConverter(ops, {
          inlineStyles: {
            size: sizeMap,
          },
        });

        setRawHtml(converter.convert());
        */
      } else {
        console.log('::::promptCard?.structured: ', promptCard?.structured);
        if (promptCard?.structured) {
          const obj = parseValidPromptBlocks(promptCard['response']) as INode[];
          if (regenerateIndex !== undefined && obj.length === 0) return;

          const delta = nodesToDelta(obj);

          console.log('::::delta.ops: ', JSON.stringify(delta.ops, null, 2));
          const html = convertDeltaToHtmlWithRefs(delta.ops as any, sizeMap);

          console.log('::::html: ', html);
          setRawHtml(html);
        } else {
          setRawHtml(await marked(promptCard['response'] ?? ''));
        }
      }
    };

    if (regenerateIndex !== undefined && !promptCard?.response) {
      setRawHtml(null);
    }

    console.log('::::processPromptCard...');
    processPromptCard();
  }, [promptCard?.response, _enableQuill]);

  console.log('::::promptCard _enableQuill: ', _enableQuill);

  useEffect(() => {
    const STYLE_ID = 'ref-btn-styles';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        div, p, li, h1, h2, h3, h4, h5, h6 { position: relative; }
        .ref-btn{
          position: absolute;
          right: -1.5rem;
          top: 50%;
          transform: translateY(-50%);
          cursor:pointer; 
          width:2rem; 
          border:0;
          background: transparent;
          border-radius: 50%;
        }
        .ref-btn:hover{ background: rgba(255,255,0,0.25); }
        .ref-btn.active{ background: rgba(255,255,0,0.3); }
        .ref-btn.active:hover{ background: rgba(255,255,0,0.25); }
      `;
      document.head.appendChild(style);
    }

    const root = document.getElementById('document-renderer');
    if (!root) return;

    root.querySelectorAll('.ref-btn').forEach((btn) => btn.remove());

    const hosts = root.querySelectorAll<HTMLElement>('[data-ref]');
    hosts.forEach((el) => {
      const ref = el.getAttribute('data-ref');
      if (!ref) return;

      const refsArray = ref
        .split(',')
        .map((s) => s.trim().replace('sid:', ''))
        .filter(Boolean);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ref-btn';

      const isActive = refsArray.every((id) => highlightedSids.includes(id));
      if (isActive) btn.classList.add('active');

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute(
        'class',
        'MuiSvgIcon-root MuiSvgIcon-fontSizeSmall css-18ajnh0-MuiSvgIcon-root',
      );
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('color', 'rgba(0,0,0,0.54)');

      const path = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path',
      );
      path.setAttribute('fill', 'rgba(0,0,0,0.54)');
      path.setAttribute(
        'd',
        'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1M8 13h8v-2H8zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5',
      );
      svg.appendChild(path);

      btn.appendChild(svg);
      btn.onclick = (e) => {
        e.stopPropagation();
        setHighlightedSids((prev) => {
          const active = refsArray.every((id) => prev.includes(id));
          return active ? [] : [...refsArray];
        });
      };

      el.appendChild(btn);
    });
  }, [rawHtml]);

  useEffect(() => {
    const root = document.getElementById('document-renderer');
    if (!root) return;

    const hosts = root.querySelectorAll<HTMLElement>('[data-ref]');
    hosts.forEach((el) => {
      const ref = el.getAttribute('data-ref');
      if (!ref) return;

      const refsArray = ref
        .split(',')
        .map((s) => s.trim().replace('sid:', ''))
        .filter(Boolean);

      const btn = el.querySelector<HTMLButtonElement>('.ref-btn');
      if (!btn) return;

      const isActive = refsArray.every((id) => highlightedSids.includes(id));
      btn.classList.toggle('active', isActive);
    });
  }, [highlightedSids]);

  return (
    <Box
      onDragOver={(e: React.DragEvent<HTMLDivElement>) =>
        handleDragOver(e, index)
      }
      onDrop={(e: React.DragEvent<HTMLDivElement>) => handleDrop(e, index)}>
      <StyledResults onClick={(e) => _enableQuill && e.stopPropagation()}>
        {rawHtml === null ? (
          noText
        ) : _enableQuill ? (
          <QuillEditor
            quillRef={quillRef}
            card={promptCard}
            setSaveEnabled={setSaveEnabled}
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: rawHtml,
            }}
          />
        )}
      </StyledResults>

      {(hoveredSection === index || promptCard.images.length > 0) && (
        <div style={{ display: 'flex', margin: '1rem' }}>
          <div style={{ display: 'ruby' }}>
            {promptCard.images.map((img, imgIndex) => {
              const image = imagesMapRef?.get(img?.id || '');
              if (!image || !image.thumb?.src) return null;

              return (
                <Grid
                  component="div"
                  item
                  key={imgIndex}
                  draggable
                  onDragStart={() => handleDragStart(index, imgIndex)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index, imgIndex)}
                  sx={styles.gridItem}>
                  <CardActionArea sx={styles.actionArea}>
                    <img
                      title={image.id}
                      src={image.thumb.src}
                      alt={`uploaded ${image.id}`}
                      style={styles.images}
                    />
                    <StyledImageOverlay sx={styles.imageOverlay}>
                      <Box className="button-overlay" sx={styles.imageOverlay2}>
                        <Delete
                          sx={styles.delete}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(e, index, imgIndex);
                          }}
                        />
                        <Edit
                          sx={styles.edit}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectImage(e, index, imgIndex);
                          }}
                        />
                      </Box>
                    </StyledImageOverlay>
                  </CardActionArea>
                </Grid>
              );
            })}
          </div>
          {hoveredSection === index && (
            <div
              onDragOver={(e: React.DragEvent<HTMLDivElement>) =>
                handleDragOver(e, index)
              }
              onDragEnd={handleDragLeave}
              style={styles.hiddenDiv as CSSProperties}>
              <Typography sx={styles.dropImage}>Drop Image</Typography>
            </div>
          )}
        </div>
      )}
    </Box>
  );
};

export default PromptBlock;
